import {
    ApolloClient,
    ApolloLink,
    InMemoryCache,
    HttpLink,
} from '@apollo/client';
import { ErrorLink } from '@apollo/client/link/error';
import { SetContextLink } from '@apollo/client/link/context';
import { env } from '@/lib/env';
import { getAccessToken } from '@/lib/auth/tokenStore';

/**
 * The Apollo Client for the GraphQL half of the Dawn Patrol API
 * (`POST /graphql`). The Express half — auth, OAuth, `/me`, `/spots`,
 * `/subscription` — goes through `@/lib/api/httpClient` instead. Same origin,
 * different path.
 *
 * Built by a factory (not a module-level singleton) so tests and Storybook can
 * create isolated clients with their own link/cache and no shared state.
 */
export function createApolloClient(
    options: { uri?: string; link?: HttpLink } = {}
): ApolloClient {
    const httpLink =
        options.link ??
        new HttpLink({
            uri: options.uri ?? env.graphqlEndpoint,
        });

    // The API authenticates with `Authorization: Bearer <token>` — there is no
    // cookie session, so the header has to be attached per request. Read from
    // the store at call time, never captured at client construction, or the
    // client would keep sending whatever token existed at startup.
    const authLink = new SetContextLink((prevContext) => {
        const token = getAccessToken();
        if (!token) {
            return prevContext;
        }

        return {
            ...prevContext,
            headers: {
                ...prevContext.headers,
                Authorization: `Bearer ${token}`,
            },
        };
    });

    const errorLink = new ErrorLink(({ error, operation }) => {
        // Transport/network failures and GraphQL errors both land here. This is
        // observability only — it shows the user nothing. Each hook's consumer
        // renders its own error state.
        console.error(
            `[graphql] operation "${operation.operationName}" failed:`,
            error
        );
    });

    return new ApolloClient({
        link: ApolloLink.from([errorLink, authLink, httpLink]),
        cache: new InMemoryCache(),
        devtools: { enabled: env.isDevelopment },
    });
}
